import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePublicMembershipEnabled } from "../components/PreLaunchGuard";
import { getArticles, getCategories, getDestinations, getDeals, getSiteSections, getBanners, getPartners, getProductReviews } from "../lib/api";
import { Button } from "../components/ui/button";
import ArticleCard from "../components/ArticleCard";
import PrivilegePopup from "../components/PrivilegePopup";
import {
  Ship, Building, UtensilsCrossed, MapPin, ArrowRight,
  Users, Globe, Heart, Award, Anchor, Compass, Star, Package
} from "lucide-react";

const categoryIcons = {
  "croisieres": Ship,
  "hotels": Building,
  "restaurants": UtensilsCrossed,
  "destinations": MapPin,
  "conseils-voyage": Compass
};

const categoryImages = {
  "croisieres": "https://images.pexels.com/photos/813011/pexels-photo-813011.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
  "hotels": "https://images.pexels.com/photos/237371/pexels-photo-237371.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
  "restaurants": "https://images.pexels.com/photos/67468/pexels-photo-67468.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
  "destinations": "https://images.pexels.com/photos/3225531/pexels-photo-3225531.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
};

export default function HomePage() {
  const { t } = useTranslation();
  const publicMembership = usePublicMembershipEnabled();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [deals, setDeals] = useState([]);
  const [partners, setPartners] = useState([]);
  const [sections, setSections] = useState([]);
  const [banners, setBanners] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ articlesCount: 0, partnersCount: 0, hasEnoughContent: false });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [articlesRes, categoriesRes, destinationsRes, dealsRes, sectionsRes, bannersRes, partnersRes, reviewsRes] = await Promise.all([
        getArticles({ limit: 6 }),
        getCategories(),
        getDestinations(),
        getDeals().catch(() => []),
        getSiteSections().catch(() => ({ data: [] })),
        getBanners({ position: 'homepage', active: true }).catch(() => []),
        getPartners().catch(() => []),
        getProductReviews({ limit: 3, status: 'published' }).catch(() => [])
      ]);

      const articlesData = Array.isArray(articlesRes) ? articlesRes : (articlesRes.data || []);
      const categoriesData = Array.isArray(categoriesRes) ? categoriesRes : (categoriesRes.data || []);
      const destinationsData = Array.isArray(destinationsRes) ? destinationsRes : (destinationsRes.data || []);
      const dealsData = Array.isArray(dealsRes) ? dealsRes : (dealsRes.data || []);
      const sectionsData = Array.isArray(sectionsRes) ? sectionsRes : (sectionsRes.data || []);
      const bannersData = Array.isArray(bannersRes) ? bannersRes : (bannersRes.data || []);
      const partnersData = Array.isArray(partnersRes) ? partnersRes : (partnersRes.data || []);
      const reviewsData = Array.isArray(reviewsRes) ? reviewsRes : (reviewsRes.data || []);

      setArticles(articlesData);
      setCategories(categoriesData);
      setDestinations(destinationsData);
      setDeals(dealsData);
      setSections(sectionsData);
      setBanners(bannersData);
      setPartners(partnersData);
      setReviews(reviewsData.filter(r => r.status === 'published'));

      const publishedArticles = articlesData.filter(a => a.status === 'published').length;
      const activePartners = partnersData.filter(p => p.is_active !== false).length;
      setStats({
        articlesCount: publishedArticles,
        partnersCount: activePartners,
        hasEnoughContent: publishedArticles >= 5 || activePartners >= 2
      });
    } catch (error) {
      console.error("Error loading homepage data:", error);
    } finally {
      setLoading(false);
    }
  };

  const isSectionEnabled = (sectionSlug) => {
    const section = sections.find(s => s.slug === sectionSlug);
    if (!section) return true;
    return section.show_on_home !== false;
  };

  const mainCategorySlugs = ["croisieres", "hotels", "restaurants"];
  const uniqueCategories = categories.reduce((acc, cat) => {
    if (!acc.find(c => c.slug === cat.slug)) acc.push(cat);
    return acc;
  }, []);
  const visibleCategories = uniqueCategories
    .filter(cat => mainCategorySlugs.includes(cat.slug) && isSectionEnabled(cat.slug))
    .slice(0, 3);

  const visibleDeals = deals.filter(d => {
    if (!d.is_published) return false;
    const status = d.status || "brouillon";
    if (!["teaser_public", "actif"].includes(status)) return false;
    if (d.valid_until) {
      const expiryDate = new Date(d.valid_until);
      if (expiryDate < new Date()) return false;
    }
    return true;
  }).sort((a, b) => {
    if (a.show_on_home && !b.show_on_home) return -1;
    if (!a.show_on_home && b.show_on_home) return 1;
    return 0;
  }).slice(0, 3);

  const featuredArticle = articles.find(a => a.featured) || articles[0];
  const otherArticles = articles.filter(a => a.id !== featuredArticle?.id).slice(0, 5);
  const hasDeals = visibleDeals.length > 0;
  const hasReviews = reviews.length > 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="flex flex-col items-center gap-4">
          <Anchor className="h-12 w-12 text-accent animate-pulse" />
          <p className="text-muted-foreground font-body">{t("home.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-secondary flex flex-col">
      <PrivilegePopup />
      {/* Hero */}
      <section className="order-1 relative min-h-[90vh] flex items-end" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/3601425/pexels-photo-3601425.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&dpr=2" alt="Voyage de luxe" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/40 to-transparent" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pb-20 md:pb-28">
          <div className="max-w-3xl">
            <p className="font-accent text-accent text-lg md:text-xl italic mb-4 animate-fade-in" style={{ animationDelay: "0.2s" }}>{t("home.tagline")}</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-none mb-8 animate-slide-up" style={{ animationDelay: "0.3s" }}>
              {t("home.heroTitle1")}
              <span className="block text-accent">{t("home.heroTitle2")}</span>
            </h1>
            <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-10 max-w-xl font-body animate-slide-up" style={{ animationDelay: "0.4s" }}>{t("home.heroLead")}</p>
            <div className="flex flex-wrap gap-4 animate-slide-up" style={{ animationDelay: "0.5s" }}>
              <Link to="/recherche">
                <Button className="bg-accent hover:bg-accent/90 text-white px-8 py-6 text-base font-semibold rounded-full shadow-lg hover:shadow-xl transition-all" data-testid="hero-explore-btn">
                  {t("home.ctaReadArticles")}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              {publicMembership && (
                <Link to="/adherer">
                  <Button variant="outline" className="border-2 border-white/80 text-white hover:bg-white hover:text-primary px-8 py-6 text-base font-semibold rounded-full transition-all" data-testid="hero-join-btn">
                    {t("home.becomeMember")}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="order-6 py-20 md:py-28 bg-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            <div className="text-center p-6 md:p-10 border border-border/30 rounded-2xl bg-gradient-to-br from-secondary/50 to-transparent hover:shadow-lg transition-all duration-300 animate-slide-up" style={{ animationDelay: "0s" }}>
              <Globe className="h-5 w-5 mx-auto mb-4 text-accent" />
              <p className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-2">{stats.articlesCount > 0 ? `${stats.articlesCount}` : "—"}</p>
              <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-muted-foreground font-body font-medium">{stats.articlesCount > 0 ? t("home.statsArticlesPublished") : t("home.statsStoriesSoon")}</p>
            </div>
            <div className="text-center p-6 md:p-10 border border-border/30 rounded-2xl bg-gradient-to-br from-secondary/50 to-transparent hover:shadow-lg transition-all duration-300 animate-slide-up" style={{ animationDelay: "0.1s" }}>
              <Users className="h-5 w-5 mx-auto mb-4 text-accent" />
              <p className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-2">39€</p>
              <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-muted-foreground font-body font-medium">{t("home.statsAnnualMembership")}</p>
            </div>
            <div className="text-center p-6 md:p-10 border border-border/30 rounded-2xl bg-gradient-to-br from-secondary/50 to-transparent hover:shadow-lg transition-all duration-300 animate-slide-up" style={{ animationDelay: "0.2s" }}>
              <Heart className="h-5 w-5 mx-auto mb-4 text-accent" />
              <p className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-2">{stats.partnersCount > 0 ? `${stats.partnersCount}` : "—"}</p>
              <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-muted-foreground font-body font-medium">{stats.partnersCount > 0 ? t("home.statsPartnersActive") : t("home.statsPartnersSoon")}</p>
            </div>
            <div className="text-center p-6 md:p-10 border border-border/30 rounded-2xl bg-gradient-to-br from-secondary/50 to-transparent hover:shadow-lg transition-all duration-300 animate-slide-up" style={{ animationDelay: "0.3s" }}>
              <Award className="h-5 w-5 mx-auto mb-4 text-accent" />
              <p className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-2">100%</p>
              <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-muted-foreground font-body font-medium">{t("home.statsAuthentic")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Univers */}
      {isSectionEnabled("univers") && visibleCategories.length > 0 && (
        <section className="order-5 py-24 md:py-32" data-testid="univers-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16 md:mb-20">
              <p className="text-xs uppercase tracking-[0.2em] text-accent font-body font-medium mb-4">{t("home.universKicker")}</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-6">{t("home.universTitle")}</h2>
              <p className="text-muted-foreground font-body max-w-2xl mx-auto text-base md:text-lg">{t("home.universLead")}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {visibleCategories.map((category, index) => {
                const Icon = categoryIcons[category.slug] || MapPin;
                const imageUrl = categoryImages[category.slug] || category.cover_image;
                const description = t(`home.catDesc.${category.slug}`, { defaultValue: t("home.discover") });
                return (
                  <Link key={category.id || category.slug} to={`/categorie/${category.slug}`} className="group relative aspect-[3/4] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 animate-slide-up" style={{ animationDelay: `${index * 0.15}s` }} data-testid={`category-card-${category.slug}`}>
                    <img src={imageUrl} alt={category.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/30 to-transparent opacity-90 group-hover:opacity-80 transition-opacity" />
                    <div className="absolute bottom-0 left-0 right-0 p-8">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-full bg-accent/20 backdrop-blur-md flex items-center justify-center border border-white/20">
                          <Icon className="h-6 w-6 text-accent" />
                        </div>
                      </div>
                      <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2 group-hover:text-accent transition-colors">{category.name}</h3>
                      <p className="text-white/70 text-sm font-body mb-3">{description}</p>
                      <div className="flex items-center gap-2 text-accent text-sm font-medium">
                        {category.article_count > 0 ? (<span>{category.article_count} {t("home.articlesCountSuffix")}</span>) : (<span>{t("home.soonAvailable")}</span>)}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Bons Plans */}
      {isSectionEnabled("bons-plans") && hasDeals && (
        <section className="order-7 py-24 md:py-32 bg-white" data-testid="deals-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-accent font-body font-medium mb-4">{t("home.dealsKicker")}</p>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary">{t("home.dealsTitle")}</h2>
              </div>
              <Link to="/bons-plans" className="flex items-center gap-2 text-primary hover:text-accent transition-colors font-medium font-body group">
                {t("home.dealsSeeAll")}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {visibleDeals.map((deal, index) => (
                <div key={deal.id} className="group bg-secondary rounded-2xl overflow-hidden border border-border/30 hover:shadow-xl hover:border-accent/20 transition-all duration-300 animate-slide-up" style={{ animationDelay: `${index * 0.15}s` }} data-testid={`deal-card-${deal.id}`}>
                  <div className="aspect-[16/10] relative overflow-hidden">
                    <img src={deal.partner_logo || `https://images.pexels.com/photos/237371/pexels-photo-237371.jpeg?auto=compress&cs=tinysrgb&w=600`} alt={deal.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    {deal.discount && (<div className="absolute top-4 right-4 bg-accent text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">{deal.discount}</div>)}
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="p-6 md:p-8">
                    <p className="text-xs uppercase tracking-[0.15em] text-accent font-body font-medium mb-3">{deal.partner_name}</p>
                    <h3 className="font-heading text-xl font-bold text-primary mb-3 group-hover:text-accent transition-colors">{deal.title}</h3>
                    <p className="text-muted-foreground text-sm font-body leading-relaxed mb-5 line-clamp-2">{deal.description}</p>
                    {deal.is_members_only && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent bg-accent/10 px-4 py-2 rounded-full">
                        <Star className="h-3 w-3 fill-current" /> {t("home.membersOnly")}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Tests Produits */}
      {isSectionEnabled("tests-produits") && (
        <section className="order-4 py-24 md:py-32" data-testid="reviews-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Package className="h-4 w-4" />
                  {t("home.testsBadge")}
                </div>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">{t("home.testsTitle")}</h2>
                <p className="text-muted-foreground max-w-lg font-body text-base md:text-lg">{t("home.testsLead")}</p>
              </div>
              <Link to="/tests-produits">
                <Button variant="outline" className="border-2 border-primary/20 hover:border-accent hover:text-accent px-6 py-5 rounded-full font-semibold transition-all whitespace-nowrap">
                  {t("home.testsSeeAll")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            {hasReviews ? (
              <div className="grid md:grid-cols-3 gap-8">
                {reviews.slice(0, 3).map((review) => (
                  <Link key={review.id} to={`/tests-produits/${review.id}`} className="group">
                    <div className="bg-white rounded-2xl overflow-hidden border border-border/50 hover:border-accent/30 hover:shadow-xl transition-all duration-300">
                      <div className="aspect-[16/10] overflow-hidden">
                        <img src={review.photos?.[0]?.cloudinary_url || "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg"} alt={review.product_name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </div>
                      <div className="p-6">
                        <span className="inline-block text-xs uppercase tracking-wider text-accent font-medium mb-2">{review.category || t("home.testProduct")}</span>
                        <h3 className="font-heading text-lg font-bold text-primary mb-2 line-clamp-2 group-hover:text-accent transition-colors">{review.product_name}</h3>
                        {review.rating && (
                          <div className="flex items-center gap-1 mb-2">
                            {[...Array(5)].map((_, i) => (<Star key={i} className={`h-4 w-4 ${i < review.rating ? 'text-accent fill-accent' : 'text-muted-foreground/30'}`} />))}
                          </div>
                        )}
                        <p className="text-muted-foreground text-sm line-clamp-2 font-body">{review.summary || review.verdict}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-secondary/30 rounded-2xl border border-border/30">
                <Package className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
                <p className="text-muted-foreground font-body">{t("home.testsSoon")}</p>
                <p className="text-muted-foreground/70 text-sm mt-2">{t("home.testsSoonSub")}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Featured */}
      {isSectionEnabled("featured") && featuredArticle && (
        <section className="order-2 py-24 md:py-32" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-center">
              <div className="lg:col-span-7">
                <Link to={`/article/${featuredArticle.slug || featuredArticle.id}`} className="block group">
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                    <img src={featuredArticle.photos?.[0]?.cloudinary_url || "https://images.pexels.com/photos/3601425/pexels-photo-3601425.jpeg"} alt={featuredArticle.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                </Link>
              </div>
              <div className="lg:col-span-5">
                <p className="text-xs uppercase tracking-[0.2em] text-accent font-body font-medium mb-4">{t("home.featuredKicker")}</p>
                <Link to={`/article/${featuredArticle.slug || featuredArticle.id}`}>
                  <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-6 hover:text-accent transition-colors leading-tight">{featuredArticle.title}</h2>
                </Link>
                <p className="text-muted-foreground font-body leading-relaxed mb-8 line-clamp-4">{featuredArticle.excerpt}</p>
                <div className="flex items-center gap-4 mb-8">
                  <span className="text-sm text-muted-foreground font-body">{featuredArticle.category_name}</span>
                  {featuredArticle.destination_name && (
                    <>
                      <span className="text-muted-foreground/30">•</span>
                      <span className="text-sm text-muted-foreground font-body">{featuredArticle.destination_name}</span>
                    </>
                  )}
                </div>
                <Link to={`/article/${featuredArticle.slug || featuredArticle.id}`}>
                  <Button variant="outline" className="border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-6 rounded-full font-semibold transition-all" data-testid="featured-read-btn">
                    {t("home.readArticle")}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Recent Articles */}
      {isSectionEnabled("articles") && otherArticles.length > 0 && (
        <section className="order-3 py-24 md:py-32 bg-white" data-testid="articles-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-accent font-body font-medium mb-4">{t("home.recentKicker")}</p>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary">{t("home.recentTitle")}</h2>
              </div>
              <Link to="/recherche" className="hidden md:flex items-center gap-2 text-primary hover:text-accent transition-colors font-medium font-body group">
                {t("home.allArticles")}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {otherArticles.map((article, index) => (
                <div key={article.id} className="animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <ArticleCard article={article} />
                </div>
              ))}
            </div>
            <div className="mt-16 text-center md:hidden">
              <Link to="/recherche">
                <Button variant="outline" className="gap-2 rounded-full px-8 py-6">
                  {t("home.allArticles")}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA — masqué quand `public_membership_enabled=false` (mode éditorial public). */}
      {publicMembership && (
      <section className="order-8 relative py-32 md:py-40 overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/97083/pexels-photo-97083.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&dpr=2" alt="Expérience premium" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-primary/70" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-10 md:p-16 text-center border border-white/20">
            <p className="font-accent text-accent text-lg md:text-xl italic mb-4">{t("home.ctaJoinKicker")}</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">{t("home.ctaJoinTitle")}</h2>
            <p className="text-white/80 text-lg md:text-xl font-body max-w-2xl mx-auto mb-4 leading-relaxed">{t("home.ctaJoinLead")}</p>
            <p className="text-accent text-4xl md:text-5xl font-heading font-bold mb-10">39€<span className="text-2xl">{t("home.perYear")}</span></p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/adherer">
                <Button className="bg-accent hover:bg-accent/90 text-white px-10 py-7 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all w-full sm:w-auto" data-testid="cta-join-btn">
                  {t("home.becomeMember")}
                  <ArrowRight className="ml-3 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/association">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary px-10 py-7 text-lg font-semibold rounded-full transition-all w-full sm:w-auto">
                  {t("home.learnMore")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
      )}
    </div>
  );
}
